<template>
    <div class="address-form">
        <form @submit.prevent="base_submit('model', 'submit', null)">

            <div class="title">{{title}}</div>

            <div v-if="warn" class="info pb-3" v-html="warn"></div>

            <div v-if="info" class="info pb-3" v-html="info"></div>

            <form-select :model-value="model.country"
                         select-class="form-control form-control-sm"
                         :label="t('model.country.label')"
                         :options="countries"
                         option-value="alpha3"
                         :disabled="true"/>

            <div v-for="(field, index) in address_form_getAddressFields(config, {tags: tags})" :key="field.field">

                <form-textbox v-if="fieldTypes.text === field.type"
                              textbox-class="form-control form-control-sm"
                              v-model="model[field.field]"
                              :validator="v$.model[field.field]"
                              :label="address_form_getAddressFieldTranslation(field, 'label', model.country)"
                              :placeholder="address_form_getAddressFieldTranslation(field, 'placeholder', model.country)"
                              :focus="0==index"
                              :errors="errors[field.field]"
                              :mask="field.mask"
                              :maxlength="field.maxlength"
                              :max-characters="'undefined'===typeof field.mask?field.maxlength:null"
                              :disabled="(field.disabled && !(initialModel && v$.initialModel[field.field].$invalid)) || processing"/>

                <form-textarea v-if="fieldTypes.textarea === field.type"
                               textarea-class="form-control form-control-sm"
                               v-model="model[field.field]"
                               :validator="v$.model[field.field]"
                               :label="address_form_getAddressFieldTranslation(field, 'label', model.country)"
                               :placeholder="address_form_getAddressFieldTranslation(field, 'placeholder', model.country)"
                               :focus="0==index"
                               :errors="errors[field.field]"
                               :maxlength="field.maxlength"
                               :rows="field.rows"
                               :max-characters="'undefined'===typeof field.mask?field.maxlength:null"
                               :disabled="(field.disabled && !(initialModel && v$.initialModel[field.field].$invalid)) || processing"/>

                <form-select v-if="fieldTypes.dropdown === field.type"
                             select-class="form-control form-control-sm"
                             v-model="model[field.field]"
                             :validator="v$.model[field.field]"
                             :label="address_form_getAddressFieldTranslation(field, 'label', model.country)"
                             :options="address_form_getAddressFieldOptions(field)"
                             :focus="0===index"
                             :errors="errors[field.field]"
                             :disabled="(field.disabled && !(initialModel && v$.initialModel[field.field].$invalid)) || processing"/>

                <form-date v-if="fieldTypes.datepicker === field.type"
                           select-class="form-control form-control-sm"
                           v-model="model[field.field]"
                           :validator="v$.model[field.field]"
                           :label="address_form_getAddressFieldTranslation(field, 'label', model.country)"
                           :min-date="address_form_getAddressFieldDatepickerMinDate(field)"
                           :max-date="address_form_getAddressFieldDatepickerMaxDate(field)"
                           :disable-dates="address_form_getAddressFieldDatepickerDisableDates(field)"
                           :milliseconds="true"
                           :disabled="(field.disabled && !(initialModel && v$.initialModel[field.field].$invalid)) || processing"/>
            </div>

            <div class="mb-3 small" v-if="displayTFADisclaimer">{{t('disclaimer')}}</div>

            <div class="mb-3">
                <div class="row">
                    <div class="col-6">
                        <button type="submit" class="btn btn-sm btn-primary" :disabled="processing">
                            <spinner v-if="saving"></spinner>
                            {{saving ? t('buttons.submit.saving') : t('buttons.submit.text')}}
                        </button>
                    </div>
                    <div v-if="!hideCancel" class="col-6 text-end">
                        <button type="button" class="btn btn-sm btn-outline-primary"
                                :disabled="processing"
                                @click="$emit('cancel')">
                            {{t('buttons.cancel.text')}}
                        </button>
                    </div>
                </div>
            </div>

        </form>

    </div>
</template>

<script>

    import useVuelidate from "@vuelidate/core";
    import {mapGetters} from "vuex"

    import mixin_address from "@/mixins/address"
    import mixin_address_form from "@/mixins/address/form";
    import {ADDRESS_TFA} from "@/constants";
    import {sanitize} from "@/helpers/html";

    import FormTextbox from "@/components/form/FormTextbox";
    import FormSelect from "@/components/form/FormSelect";
    import FormTextarea from "@/components/form/FormTextarea";
    import FormDate from "@/components/form/FormDate.vue";

    const fieldTypes = {
        text: "text",
        textarea: "textarea",
        dropdown: "dropdown",
        datepicker: "datepicker"
    };

    const model = {};

    export default {
        name: "AddressForm",
        setup() {
            return {
                v$: useVuelidate()
            }
        },
        mixins: [mixin_address, mixin_address_form],
        components: {
            FormDate,
            FormTextarea,
            FormTextbox,
            FormSelect
        },
        props: {
            disabled: {
                type: Boolean,
                default: false
            },
            value: {
                type: Object
            },
            saving: {
                type: Boolean,
                default: false
            },
            tags: {
                type: Array,
                default: () => []
            },
            hideCancel: Boolean
        },
        data() {

            const fields = this.address_form_getAddressFields(
                this.$store.getters["user/address/config"],
                {tags: this.tags}
            );
            const country = this.$store.getters["user/profile/country"];
            const prepareModel = (value) => ({
                country,
                ...fields.reduce((model, field) => {

                    if (value && value[field.field]) {

                        if (fieldTypes.dropdown === field.type) {

                            const option = this
                                .address_form_getAddressFieldOptions(field)
                                .find(option => option.value == value[field.field]);

                            model[field.field] = option ? value[field.field] : null;
                        } else {

                            model[field.field] = value[field.field];
                        }
                    } else if ("name" === field.field) {

                        model[field.field] = this.$store.getters["user/profile/name"]
                            .split(" ")
                            .map(string => string.charAt(0).toUpperCase() + string.slice(1))
                            .join(" ");
                    }

                    return model
                }, {})
            });

            return {
                t_path: "components.address.form",
                processing: false,
                fieldTypes,
                initialModel: prepareModel(this.value ? this.value : {}),
                model: prepareModel(this.value ? this.value : {})
            }
        },
        computed: {
            ...mapGetters({
                countries: "db/countries/items"
            }),
            config() {

                if (this.incomplete) {

                    return this.$store
                        .getters["user/address/config"]
                        .map(field => ({
                            ...field,
                            disabled: !(field.validation && field.validation.required && !this.value[field.field])
                        }));
                }

                return this.$store.getters["user/address/config"];
            },
            incomplete() {

                return this.value && this.address_isIncompleted(this.value, {tags: this.tags});
            },
            title() {

                if ("undefined" === typeof this.value) {

                    return this.t("title.new");
                }

                return this.incomplete ? this.t("title.complete") : this.t("title.edit");
            },
            warn() {

                if (this.incomplete) {

                    return this.t("incomplete");
                }
            },
            displayTFADisclaimer() {

                return ADDRESS_TFA && 0 < this.$store.getters["user/address/items"].length;
            },
            info() {

                if (this.$store.getters["user/address/header"]) {

                    return sanitize(this.$store.getters["user/address/header"]);
                }

                if (this.te(`info.${this.model.country}`)) {

                    return this.t(`info.${this.model.country}`);
                }

                return null;
            },
            errors() {

                return this.address_form_getErrorMessages(
                    this.address_form_getAddressFields(this.config, {tags: this.tags}),
                    this.model.country
                );
            }
        },
        validations() {

            return {
                model: this.address_form_createValidationRules(
                    this.address_form_getAddressFields(this.config, {tags: this.tags})
                ).model,
                initialModel: this.address_form_createValidationRules(
                    this.address_form_getAddressFields(this.config, {tags: this.tags})
                ).model
            }
        },
        methods: {
            submit() {

                this.processing = true;

                return (this.value ? this.put() : this.post())
                    .catch(error => this.base_error(error))
                    .finally(() => this.processing = false);
            },
            post() {

                const message = this.t("message.added");

                return this.$store
                    .dispatch("user/address/post", {
                        ...this.address_form_getPayload(
                            this.address_form_getAddressFields(this.config, {tags: this.tags}),
                            this.model
                        ),
                        redirectUrl: `${window.location.origin}${this.$router.resolve({name: "address.verified"}).fullPath}`,
                        country: this.$store.getters["user/profile/country"]
                    })
                    .then(address => {
                        this.$emit("saved", address._id);
                        this.$toast.success(message);
                    });
            },
            put() {

                const message = this.t("message.updated");

                return this.$store
                    .dispatch("user/address/put", {
                        ...this.value,
                        ...this.address_form_getPayload(
                            this.address_form_getAddressFields(this.config, {tags: this.tags}),
                            this.model
                        ),
                        country: this.$store.getters["user/profile/country"]
                    })
                    .then(address => {
                        this.$emit("saved", address._id);
                        this.$toast.success(message);
                    });
            }
        }
    }
</script>